import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCampaignManagerBookings } from './useCampaignManagerBookings';
import useAbortableEffect from '../../../hooks/useAbortableEffect';
import { showNotification } from '../../../utils/showNotification';
import CampaignManagerBookingsTable, {
    CampaignManagerBookingsTableRow,
} from './BookingsTable/CampaignManagerBookingsTable';
import CreateCampaignManagerBookingModal from './CreateBookingModal/CreateCampaignManagerBookingModal';
import styles from './CampaignManagerBookings.module.css';
import useOnScroll from '../../../hooks/useOnScroll';
import { DropdownOptionWithSubLabel, OptionWithSubLabel, Select } from '@round/ui-kit';
import {
    BookingStatus,
    getCampaignManagerInfluencer,
    getCampaignManagerInfluencers,
    getCampaignManagerCampaign,
    getCampaignManagerCampaigns,
    getCampaignManagerClients,
    getCampaignManagerClient,
    CreateAndSendCampaignManagerInvoiceData,
    PatchCampaignManagerBookingData,
} from '@round/api';
import useUrlState from '../../../hooks/useUrlState';
import Badge from '../../../ui/Badge/Badge';
import Checkbox from '../../../ui/DataEntry/Checkbox/Checkbox';
import cn from 'classnames';
import { ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../utils/utils.types';
import { useSelectPaginatedOptions } from '../../../hooks/useSelectPaginatedOptions';
import { bookingsFilterStyles } from '../../booking/helpers';
import ButtonOld from '../../../ui/Button/Button';
import DatePickerSelect from '../../../ui/DataEntry/DatePicker/DatePickerSelect/DatePickerSelect';
import { formatToIsoDateString } from '../../../utils/utits';
import ApproveToBillModal from '../../booking/ApproveToBillModal/ApproveToBillModal';
import Modal from '../../../ui-new/Modal/Modal';
import Button from '../../../ui-new/Button/Button';
import CreateCampaignManagerInvoiceModal from './CreateInvoice/CreateCampaignManagerInvoiceModal';

const CampaignManagerBookings = () => {
    const [isApproveToBillModalOpen, setIsApproveToBillModalOpen] = useState(false);
    const [isConfirmApproveToBillModalOpen, setIsConfirmApproveToBillModalOpen] = useState(false);
    const [currentApproveToBillBooking, setCurrentApproveToBillBooking] =
        useState<CampaignManagerBookingsTableRow | null>(null);

    const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const [urlState, setUrlState] = useUrlState<{
        exclude_declined: boolean;
        influencer_id: number | undefined;
        campaign_id: number | undefined;
        my_bookings: boolean;
        client_id: number | undefined;
        date_created_start: string | undefined;
        date_created_end: string | undefined;
        date_approved_start: string | undefined;
        date_approved_end: string | undefined;
    }>(
        {
            exclude_declined: true,
        },
        {
            exclude_declined: 'boolean',
            influencer_id: 'number',
            campaign_id: 'number',
            my_bookings: 'boolean',
            client_id: 'number',
        }
    );

    const {
        isInitialized: isBookingsInitialized,
        init: initBookings,
        reset: resetBookings,
        ...bookingsDataProvider
    } = useCampaignManagerBookings({
        page_size: 25,
        excluded_statuses: urlState.exclude_declined ? 'declined' : undefined,
        influencer_id: urlState.influencer_id,
        campaign_id: urlState.campaign_id,
        my_bookings: urlState.my_bookings,
        client_id: urlState.client_id,
        date_created_start: urlState.date_created_start,
        date_created_end: urlState.date_created_end,
        date_approved_start: urlState.date_approved_start,
        date_approved_end: urlState.date_approved_end,
    });

    useAbortableEffect(
        (signal) => {
            if (!isBookingsInitialized) {
                initBookings({ signal }).catch(() => {
                    showNotification('Could not load data', 'error');
                });
            }
        },
        [isBookingsInitialized, initBookings]
    );

    const bookingsTableRows: CampaignManagerBookingsTableRow[] = useMemo(
        () =>
            bookingsDataProvider.bookings.map((booking) => {
                const influencer = bookingsDataProvider.influencers.find(
                    (influencer) => influencer.id === booking.influencer_id
                );
                return {
                    ...booking,
                    influencer,
                };
            }),
        [bookingsDataProvider.bookings, bookingsDataProvider.influencers]
    );

    const { props: creatorSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getCampaignManagerInfluencers
    >({
        fetchOptions: getCampaignManagerInfluencers,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const [selectedCreatorOption, setSelectedCreatorOption] =
        useState<ValueType<GenericDropdownOption<number>, false>>(null);

    const handleSetSelectedCreatorOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false>) => {
            setUrlState({ influencer_id: option?.value });
            setSelectedCreatorOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialCreator = useCallback(async (creatorId: number) => {
        try {
            const response = await getCampaignManagerInfluencer(creatorId);
            if (response.status !== 200) {
                showNotification('Could not load Creator', 'error');
                return;
            }
            setSelectedCreatorOption({
                value: creatorId,
                label: response.data.name,
            });
        } catch {
            showNotification('Could not load Creator', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.influencer_id || selectedCreatorOption?.value === urlState.influencer_id) {
            return;
        }

        const creatorOption = creatorSelectProps.options.find((option) => option.value === urlState.influencer_id);

        if (!creatorOption) {
            fetchInitialCreator(urlState.influencer_id);
        }
    }, [fetchInitialCreator, creatorSelectProps.options, urlState.influencer_id, selectedCreatorOption]);

    const [selectedCampaignOption, setSelectedCampaignOption] =
        useState<ValueType<DropdownOptionWithSubLabel<number>, false>>(null);

    const { props: campaignSelectProps } = useSelectPaginatedOptions<
        DropdownOptionWithSubLabel<number>,
        typeof getCampaignManagerCampaigns
    >({
        fetchOptions: getCampaignManagerCampaigns,
        mapToOption: (item) => ({
            value: item.id,
            label: item.name,
            subLabel: item.client_name,
        }),
    });

    const handleSetSelectedCampaignOption = useCallback(
        (option: ValueType<DropdownOptionWithSubLabel<number>, false>) => {
            setUrlState({ campaign_id: option?.value });
            setSelectedCampaignOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialCampaign = useCallback(async (campaignId: number) => {
        try {
            const response = await getCampaignManagerCampaign(campaignId);
            if (response.status !== 200) {
                showNotification('Could not load campaign', 'error');
                return;
            }
            setSelectedCampaignOption({
                value: response.data.id,
                label: response.data.name,
                subLabel: response.data.client_name,
            });
        } catch {
            showNotification('Could not load campaign', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.campaign_id || selectedCampaignOption?.value === urlState.campaign_id) {
            return;
        }

        const campaignOption = campaignSelectProps.options.find((option) => option.value === urlState.campaign_id);

        if (!campaignOption) {
            fetchInitialCampaign(urlState.campaign_id);
        }
    }, [fetchInitialCampaign, campaignSelectProps.options, urlState.campaign_id, selectedCampaignOption]);

    const { props: clientSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getCampaignManagerClients
    >({
        fetchOptions: getCampaignManagerClients,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const [selectedClientOption, setSelectedClientOption] = useState<ValueType<
        GenericDropdownOption<number>,
        false
    > | null>(null);

    const handleSetSelectedClientOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false> | null) => {
            setUrlState({ client_id: option?.value });
            setSelectedClientOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialClient = useCallback(async (clientId: number) => {
        try {
            const response = await getCampaignManagerClient(clientId);
            if (response.status !== 200) {
                showNotification('Could not load client', 'error');
                return;
            }
            setSelectedClientOption({
                value: response.data.id,
                label: response.data.name,
            });
        } catch {
            showNotification('Could not load client', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.client_id || selectedClientOption?.value === urlState.client_id) {
            return;
        }

        const clientOption = clientSelectProps.options.find((option) => option.value === urlState.client_id);

        if (!clientOption) {
            fetchInitialClient(urlState.client_id);
        }
    }, [fetchInitialClient, clientSelectProps.options, urlState.client_id, selectedClientOption]);

    const selectedDatesCreated: [Date, Date] | null =
        urlState.date_created_start && urlState.date_created_end
            ? [new Date(urlState.date_created_start), new Date(urlState.date_created_end)]
            : null;

    const handleSetDateCreated = useCallback(
        (dates: Date[] | null) => {
            if (!dates) {
                setUrlState({ date_created_start: undefined, date_created_end: undefined });
                resetBookings();
                return;
            }

            const [startDate, endDate] = dates;
            setUrlState({
                date_created_start: formatToIsoDateString(startDate),
                date_created_end: formatToIsoDateString(endDate),
            });
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const selectedDatesApproved: [Date, Date] | null =
        urlState.date_approved_start && urlState.date_approved_end
            ? [new Date(urlState.date_approved_start), new Date(urlState.date_approved_end)]
            : null;

    const handleSetDateApproved = useCallback(
        (dates: Date[] | null) => {
            if (!dates) {
                setUrlState({ date_approved_start: undefined, date_approved_end: undefined });
                resetBookings();
                return;
            }

            const [startDate, endDate] = dates;
            setUrlState({
                date_approved_start: formatToIsoDateString(startDate),
                date_approved_end: formatToIsoDateString(endDate),
            });

            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const onBookingsBottomReached = useOnScroll(
        () => {
            if (
                !bookingsDataProvider.isLoading &&
                !bookingsDataProvider.hasErrorLoadingData &&
                bookingsDataProvider.hasNextPage
            ) {
                bookingsDataProvider.loadNextPage().catch(() => {
                    showNotification('Could not load data', 'error');
                });
            }
        },
        { threshold: 400 }
    );

    const handleEditBooking = async (bookingId: number, data: PatchCampaignManagerBookingData) => {
        try {
            const result = await bookingsDataProvider.editBooking(bookingId, data);
            if (!result.success) {
                showNotification(result.reason || 'Could not update booking', 'error');
                return;
            }

            showNotification('Updated', 'info');
        } catch (e) {
            showNotification((e as Error).message, 'error');
            throw e;
        }
    };

    const onBookingCreated = async (bookingId: number) => {
        try {
            const result = await bookingsDataProvider.addBooking(bookingId);
            if (!result.success) {
                showNotification(result.reason || 'Could not load created booking', 'error');
                return;
            }
        } catch {
            showNotification('Could not load created booking', 'error');
        } finally {
            setIsCreateModalOpen(false);
        }
    };

    const handleSetStatus = async (bookingId: number, status: BookingStatus) => {
        try {
            const result = await bookingsDataProvider.setStatus(bookingId, status);
            if (!result.success) {
                showNotification(result.reason || 'Could not update status', 'error');
                return;
            }

            showNotification('Status updated', 'info');
        } catch (e) {
            showNotification((e as Error).message, 'error');
            throw e;
        }
    };

    const onClickApproveToBill = (booking: CampaignManagerBookingsTableRow) => {
        setCurrentApproveToBillBooking(booking);
        setIsConfirmApproveToBillModalOpen(true);
    };

    const handleApproveToBill = async (bookingId: number | undefined) => {
        if (typeof bookingId !== 'number') {
            return;
        }
        try {
            const result = await bookingsDataProvider.approveToBill(bookingId, {
                create_invoice: false,
            });
            if (!result.success) {
                showNotification(result.reason || 'Could not perform operation', 'error');
                return;
            }

            showNotification('Approved to bill', 'info');
            setIsApproveToBillModalOpen(false);

            setCurrentApproveToBillBooking(null);
        } catch (e) {
            showNotification((e as Error).message, 'error');
        }
    };

    const handleApproveToBillWithDraftInvoice = async (bookingId: number | undefined, discount?: number) => {
        if (typeof bookingId !== 'number') {
            return;
        }
        try {
            const result = await bookingsDataProvider.approveToBill(bookingId, {
                create_invoice: true,
                discount,
            });
            if (!result.success) {
                showNotification(result.reason || 'Could not perform operation', 'error');
                return;
            }

            showNotification('Approved to bill and draft invoice created', 'info');
            setIsApproveToBillModalOpen(false);
            setIsCreateInvoiceModalOpen(false);

            setCurrentApproveToBillBooking(null);
        } catch (e) {
            showNotification((e as Error).message, 'error');
        }
    };

    const handleCreateAndSendInvoice = async (data: CreateAndSendCampaignManagerInvoiceData) => {
        try {
            const result = await bookingsDataProvider.createAndSendInvoice(data);
            if (!result.success) {
                showNotification(result.reason || 'Could not create invoice', 'error');
                return;
            }

            showNotification('Invoice created and sent', 'info');
            setIsCreateInvoiceModalOpen(false);
        } catch (e) {
            showNotification('Could not create invoice', 'error');
        }
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.filtersWrapper}>
                        <Badge
                            className={cn(styles.filterBadge, styles.clickable)}
                            onClick={() => {
                                setUrlState({ my_bookings: !urlState.my_bookings });
                                resetBookings();
                            }}
                        >
                            <span>My Bookings</span>
                            <Checkbox
                                value={!!urlState.my_bookings}
                                className={cn(styles.checkbox, {
                                    [styles.checkbox_checked]: !!urlState.my_bookings,
                                })}
                            />
                        </Badge>

                        <Badge
                            className={cn(styles.filterBadge, styles.clickable)}
                            onClick={() => {
                                setUrlState({ exclude_declined: !urlState.exclude_declined });
                                resetBookings();
                            }}
                        >
                            <span>Exclude declined/cancelled</span>
                            <Checkbox
                                value={!!urlState.exclude_declined}
                                className={cn(styles.checkbox, {
                                    [styles.checkbox_checked]: !!urlState.exclude_declined,
                                })}
                            />
                        </Badge>

                        <Select
                            {...campaignSelectProps}
                            placeholder="Filter by campaign..."
                            value={selectedCampaignOption}
                            onChange={(option) => {
                                handleSetSelectedCampaignOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                            components={{
                                Option: OptionWithSubLabel,
                            }}
                        />

                        <Select
                            {...creatorSelectProps}
                            placeholder="Filter by creator..."
                            value={selectedCreatorOption}
                            onChange={(option) => {
                                handleSetSelectedCreatorOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                        />

                        <Select
                            {...clientSelectProps}
                            placeholder="Filter by client..."
                            value={selectedClientOption}
                            onChange={(option) => {
                                handleSetSelectedClientOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                        />

                        <div>
                            <span className={styles.filterLabel}>Date Created</span>
                            <DatePickerSelect
                                value={selectedDatesCreated}
                                onChange={handleSetDateCreated}
                                onClear={() => {
                                    handleSetDateCreated(null);
                                }}
                                selectRange
                                className={styles.datePickerFilter}
                            />
                        </div>

                        <div>
                            <span className={styles.filterLabel}>Date Approved</span>
                            <DatePickerSelect
                                value={selectedDatesApproved}
                                onChange={handleSetDateApproved}
                                onClear={() => {
                                    handleSetDateApproved(null);
                                }}
                                selectRange
                                className={styles.datePickerFilter}
                            />
                        </div>
                    </div>
                    <ButtonOld onClick={() => setIsCreateModalOpen(true)}>Add New Booking</ButtonOld>
                </div>

                <div className={styles.tableScrollContainer} onScroll={onBookingsBottomReached}>
                    <CampaignManagerBookingsTable
                        className={styles.table}
                        data={bookingsTableRows}
                        totals={bookingsDataProvider.totals}
                        loading={
                            bookingsDataProvider.isLoading ||
                            (!isBookingsInitialized && !bookingsDataProvider.hasErrorLoadingData)
                        }
                        updateBooking={handleEditBooking}
                        onStatusChange={handleSetStatus}
                        approveToBill={onClickApproveToBill}
                    />
                </div>
            </div>

            <CreateCampaignManagerBookingModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={onBookingCreated}
            />

            <Modal
                isOpen={isConfirmApproveToBillModalOpen}
                onClose={() => {
                    setCurrentApproveToBillBooking(null);
                    setIsConfirmApproveToBillModalOpen(false);
                }}
                closeOnOverlayClick
                closeOnEscape
            >
                <Modal.Header>Approve to Bill?</Modal.Header>

                <div>Are you sure you want to approve this booking to bill?</div>

                <Modal.Actions>
                    <Button
                        appearance="ghost"
                        onClick={() => {
                            setIsConfirmApproveToBillModalOpen(false);
                            setCurrentApproveToBillBooking(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={() => {
                            setIsConfirmApproveToBillModalOpen(false);
                            setIsApproveToBillModalOpen(true);
                        }}
                    >
                        Approve
                    </Button>
                </Modal.Actions>
            </Modal>

            <ApproveToBillModal
                isOpen={isApproveToBillModalOpen}
                onClose={() => {
                    setCurrentApproveToBillBooking(null);
                    setIsApproveToBillModalOpen(false);
                }}
                onApprove={() => handleApproveToBill(currentApproveToBillBooking?.id)}
                onCreateInvoice={() => {
                    setIsApproveToBillModalOpen(false);
                    setIsCreateInvoiceModalOpen(true);
                }}
            />

            <CreateCampaignManagerInvoiceModal
                data={
                    currentApproveToBillBooking
                        ? {
                              clientId: currentApproveToBillBooking.client_id,
                              bookingId: currentApproveToBillBooking.id,
                              bookingCost: currentApproveToBillBooking.price,
                              bookingCurrencyId: currentApproveToBillBooking.currency,
                          }
                        : null
                }
                onCreateAndSendInvoice={handleCreateAndSendInvoice}
                onSaveAsDraft={(discount) =>
                    handleApproveToBillWithDraftInvoice(currentApproveToBillBooking?.id, discount)
                }
                isOpen={isCreateInvoiceModalOpen}
                onClose={() => setIsCreateInvoiceModalOpen(false)}
            />
        </>
    );
};

export default CampaignManagerBookings;
